import { useSite } from "~/contexts";
import { useAuth } from "~/hec/context/auth-context";

import { useLoyaltyPoints } from "../../resources/loyalty-points";

/** Server-rendered initial points for the loaded product (from the PDP loader). */
export type InitialLoyaltyPoints = {
  productId: string;
  unitPrice: number;
  points: number | null;
} | null;

/**
 * Annex Cloud loyalty points preview (SFCC-11016) — replicates the live SFRA
 * element `c-product-detail__loyalty-points`: "Earn {N} {perksBrand}® Points
 * with this purchase! Sign Up", shown above the Add-to-Cart button. Per-site
 * brand from `site.loyalty` (FSA/HSA only); points (price-derived) from the
 * Annex Cloud `earnPointPdpAPI` call.
 *
 * Hybrid: the loaded product's points are server-rendered by the PDP loader and
 * passed as `initial`, so first paint needs no client round-trip. When the
 * shopper switches to a variant/price that differs from the initial, it fetches
 * live via `/resources/loyalty-points`. Renders nothing on failure, when no
 * points are returned, or when the site has no loyalty program — graceful per
 * AC#4. Points are always anonymous (`userId=""`, OQ-4); the Sign Up CTA is
 * shown to guests only (hidden once the customer is authenticated).
 */
export const LoyaltyPointsPreview = ({
  productId,
  unitPrice,
  initial,
}: {
  productId: string;
  unitPrice: number;
  initial: InitialLoyaltyPoints;
}) => {
  const { site } = useSite();
  const { isLoggedIn } = useAuth();
  const loyalty = site.loyalty;

  const matchesInitial =
    !!initial &&
    initial.productId === productId &&
    initial.unitPrice === unitPrice;

  // Fetch only when the selection diverges from the server-rendered initial.
  const fetched = useLoyaltyPoints(productId, unitPrice, !matchesInitial);

  const points = matchesInitial ? initial.points : fetched;
  if (points == null || !loyalty) return null;

  // Exact parity with the live SFRA element (`c-product-detail__loyalty-points`).
  // "Sign Up" opens the AccountDrawer via the `#register` hash (the drawer
  // listens for #login/#register), mirroring SFRA's in-place login/register
  // modal (`js-toggle-login js-create` → #signUpTab).
  return (
    <div
      className="c-product-detail__loyalty-points mb-2"
      data-testid="loyalty-points-preview"
    >
      <span className="font-grotesque text-sm font-light">
        Earn{" "}
        <strong className="inline-block min-w-[2.5ch] text-center font-medium">
          {points}
        </strong>{" "}
        {loyalty.perksBrand}
        <sup className="font-grotesque font-light">®</sup> Points with this
        purchase!
        {/* Sign Up shown to guests only — authenticated customers already have
            an account (matches the SFRA login/register CTA being guest-only). */}
        {!isLoggedIn && (
          <>
            {" "}
            <a
              href="#register"
              className="font-grotesque font-light underline"
              data-testid="pdp-loyalty-signup-link"
            >
              Sign&nbsp;Up
            </a>
          </>
        )}
      </span>
    </div>
  );
};
